import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import { readState, subscribe, unsubscribe, type PushSchritt, type PushState } from '../../lib/push'
import { useTexte, type Texte } from '../../texte'

/**
 * Was gerade läuft — in Worten, nicht als Punktereihe.
 *
 * Ein Knopf, der „Einen Moment …" sagt und dann schweigt, ist eine stille
 * Absage: aus dem Betrieb genau so gemeldet (#24, Windows).
 */
/**
 * Welcher Schritt gerade läuft.
 *
 * Der Wortlaut steht in `texte/de.ts`; hier bleibt nur die Zuordnung, damit
 * ein neuer Schritt im Ablauf nicht ohne Satz dasteht — den Typ prüft der
 * Übersetzer.
 */
const schrittText = (t: Texte, schritt: PushSchritt): string => t.push.schritte[schritt]

/**
 * Wie lange ein Schritt dauern darf, bevor die Seite dazu etwas sagt.
 *
 * Drei Sekunden: kurz genug, dass niemand ratlos davor sitzt, lang genug,
 * dass der gewöhnliche Fall — Frage kommt, Antwort kommt — ohne Zusatztext
 * durchläuft.
 */
const GEDULD = 3000

/**
 * Benachrichtigungen ein- und ausschalten.
 *
 * Gefragt wird erst auf Knopfdruck. Ungefragt darf keine Seite nach der
 * Erlaubnis fragen — und wer einmal abgelehnt hat, kann es im Browser nur
 * mühsam zurücknehmen, deshalb soll die Frage bewusst kommen.
 *
 * Die Karte erscheint nur, wenn der Server Benachrichtigungen überhaupt
 * eingerichtet hat. Ein Schalter, der nichts bewirkt, ist schlimmer als
 * keiner.
 */
export function PushCard() {
  const t = useTexte()
  const [server, setServer] = useState<{ enabled: boolean } | null>(null)
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [schritt, setSchritt] = useState<PushSchritt | null>(null)
  const [dauert, setDauert] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  // Bleibt ein Schritt stehen, sagt die Seite nach kurzer Zeit, woran es
  // liegen kann. Der Zeitgeber hängt am Schritt: jeder neue setzt ihn zurück.
  useEffect(() => {
    setDauert(false)
    if (schritt === null) return
    const uhr = window.setTimeout(() => setDauert(true), GEDULD)
    return () => window.clearTimeout(uhr)
  }, [schritt])

  useEffect(() => {
    void (async () => {
      try {
        setServer(await api.get<{ enabled: boolean }>('/api/push/key'))
        setState(await readState())
      } catch {
        setServer({ enabled: false })
      }
    })()
  }, [])

  if (!server?.enabled || !state) return null

  async function umschalten() {
    setBusy(true)
    setProblem(null)
    setMeldung(null)
    try {
      if (state?.subscribed) {
        await unsubscribe()
      } else {
        await subscribe(setSchritt)
      }
      setState(await readState())
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : t.allgemein.schiefgelaufen)
    } finally {
      setBusy(false)
      setSchritt(null)
    }
  }

  async function probe() {
    setBusy(true)
    setProblem(null)
    setMeldung(null)
    try {
      const antwort = await api.post<{ devices: number }>('/api/push/test')
      setMeldung(
        antwort.devices === 1 ? t.push.probeEines : t.push.probeMehrere(antwort.devices),
      )
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : t.allgemein.schiefgelaufen)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.push.titel}</h2>

      {!state.supported ? (
        <p className="card__lead" style={{ marginBottom: 0 }}>
          {state.needsInstall ? t.push.erstInstallieren : t.push.kannNicht}
        </p>
      ) : (
        <>
          <p className="card__lead">
            {state.subscribed ? t.push.an : t.push.aus}
          </p>

          {state.stale && (
            <p className="notice" role="alert">{t.push.veraltet}</p>
          )}

          {/* War schon einmal an und ist es nicht mehr. Das passiert ohne
              Zutun -- iOS räumt bei einer selten benutzten Web-App auf --, und
              man merkt es sonst erst daran, dass nichts mehr ankommt. Die
              Anwendung versucht es beim Start von selbst; hier steht, was
              übrig bleibt, wenn auch die Erlaubnis weg ist. */}
          {state.wanted && !state.subscribed && !state.stale && (
            <p className="notice" role="alert">{t.push.verloren}</p>
          )}

          {state.permission === 'denied' && !state.subscribed && (
            <p className="notice" role="alert">{t.push.gesperrt}</p>
          )}

          {busy && schritt !== null && (
            <p className="card__lead" aria-live="polite">{schrittText(t, schritt)}</p>
          )}

          {/* Der eine Schritt, der ohne Zutun nie zu Ende geht. Chrome und
              Edge klappen die Frage am Rechner in ein Symbol in der
              Adresszeile, statt ein Fenster zu stellen -- wer das nicht weiß,
              wartet vergeblich. */}
          {busy && schritt === 'erlaubnis' && dauert && (
            <p className="notice" role="alert">{t.push.frageOffen}</p>
          )}

          {busy && schritt !== null && schritt !== 'erlaubnis' && dauert && (
            <p className="notice" role="alert">{t.push.dauert}</p>
          )}

          {problem && <p className="notice" role="alert">{problem}</p>}
          {meldung && (
            <p className="card__lead" style={{ color: 'var(--accent)' }}>{meldung}</p>
          )}

          <button
            className="button button--outline"
            type="button"
            disabled={busy || (state.permission === 'denied' && !state.subscribed)}
            onClick={() => void umschalten()}
          >
            {busy
              ? t.allgemein.laeuft
              : state.subscribed
                ? t.push.abschalten
                : t.push.einschalten}
          </button>

          {state.subscribed && (
            <button
              className="button button--outline"
              type="button"
              style={{ marginTop: 10 }}
              disabled={busy}
              onClick={() => void probe()}
            >
              {t.push.probe}
            </button>
          )}
        </>
      )}
    </div>
  )
}
